import type { ReactNode } from 'react';
import type { To } from 'react-router-dom';
import { MdArrowBack, MdClose } from 'react-icons/md';
import { RiftboundButton, type RiftboundButtonProps } from '../ui/button';
import { useBackOrFallback } from '../../hooks/useBackOrFallback';

type BackOrFallbackButtonProps = Omit<RiftboundButtonProps, 'onClick' | 'children'> & {
  fallback: To;
  label: ReactNode;
  ariaLabel?: string;
  icon?: 'back' | 'close';
  iconOnly?: boolean;
  /** Return `false` to cancel navigation (e.g. unsaved-version prompt). */
  onBeforeNavigate?: () => boolean | void;
};

export function BackOrFallbackButton({
  fallback,
  label,
  ariaLabel,
  icon = 'back',
  iconOnly = false,
  onBeforeNavigate,
  ...buttonProps
}: BackOrFallbackButtonProps) {
  const backOrFallback = useBackOrFallback(fallback);
  const Icon = icon === 'close' ? MdClose : MdArrowBack;

  return (
    <RiftboundButton
      type="button"
      treatment="ghost"
      size="sm"
      minH="44px"
      px={iconOnly ? 2 : 3}
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      onClick={() => {
        if (onBeforeNavigate?.() === false) return;
        backOrFallback();
      }}
      {...buttonProps}
    >
      <Icon />
      {iconOnly ? null : label}
    </RiftboundButton>
  );
}
